import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Paper,
  Typography,
} from '@mui/material';
// Components
import { Link } from '@/icons/iconComponents/Link';
// Hooks
import { useFeatureFlags } from '@/providers/FeatureFlagProvider';
import { useWebiny } from '@/providers/WebinyProvider';
import { useAppInfo } from '@/providers/AppInfoProvider';
// Styles & Assets
import { colors } from '@/theme';
import { brand } from '@/brand/brand';
import styles from './offer-card.module.css';
import smartWallet from '@/assets/images/SmartWallet.png';

export default function OfferCard({ handleShareClick }): JSX.Element {
  const { shareWithFriends } = useWebiny();
  const { featureFlags }: any = useFeatureFlags();
  const { currentOffers }: any = useAppInfo();

  const defaultShareImage = brand.pages?.home?.defaultShareOfferImage || smartWallet.src;

  const defaultOffers = [
    {
      id: '1',
      title: 'Sign Up Form',
      description: '',
      image: defaultShareImage,
      imageUrl: null,
      button: {
        title: 'Share Sign Up',
        url: `${window.location.origin}/signup`,
      },
    },
  ];
  const [offers, setOffers] = useState(defaultOffers);

  const shouldShowOffers = featureFlags['Rewards.Offers.Show'];

  useEffect(() => {
    if (!shouldShowOffers) return; // If the feature flag is off, show ONLY the default offer

    // If the CMS has offers, use those
    if (shareWithFriends?.offers?.length > 0) {
      setOffers(shareWithFriends?.offers);
      return;
    }

    // If the CMS doesn't have offers, use backend offers and default offer
    const activeOffers = currentOffers.filter(
      (offer) => offer.minRedemptions !== 0 && offer.isActive
    );
    setOffers([...activeOffers, ...defaultOffers]);
  }, [currentOffers, shouldShowOffers]);

  return (
    <Paper elevation={2} sx={{ marginBottom: '24px', marginTop: '24px' }}>
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '100px',
              backgroundColor: colors.primary[100],
            }}
          >
            <Link className={styles.icon} />
          </Box>
        </Box>
        <Box>
          <Box sx={{ marginBottom: '16px' }}>
            <Typography variant="h3">{shareWithFriends.title}</Typography>
          </Box>
          <Typography variant="body2">{shareWithFriends.description}</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          listStyle: 'none',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px',
          justifyContent: 'center',
        }}
      >
        {offers.map((offer) => (
          <Card
            key={offer.id}
            sx={{
              padding: '24px',
              width: '100%',
              maxWidth: '280px',
              flex: '0 1 280px',
              display: 'flex',
              gap: '16px',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              boxShadow: 'none',
              borderRadius: '8px',
              border: `2px solid ${colors.surfaces.layerThreeBorder}`,
            }}
          >
            <CardMedia
              component="img"
              sx={{
                objectFit: 'contain',
              }}
              alt="offer image"
              width="200"
              height="180"
              image={offer?.imageUrl || offer.image}
            />
            <Box
              sx={{
                display: 'flex',
                flex: '1 0 0',
                flexDirection: 'column',
                gap: '24px',
              }}
            >
              <CardContent
                sx={{
                  padding: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 0 0',
                  gap: '8px',
                }}
              >
                <Typography variant="h4">{offer.title}</Typography>
                {offer.description && offer.description !== 'node-discount'
                  ? offer.description
                  : null}
              </CardContent>
              <CardActions sx={{ padding: '0' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleShareClick(offer?.button ?? offer)}
                  data-core-qa="shareButton"
                >
                  {offer?.button?.title ?? 'Share'}
                </Button>
              </CardActions>
            </Box>
          </Card>
        ))}
        {!brand.pages?.home?.hideMoreOffersComingSoonCard && (
          <Card
            sx={{
              padding: '24px',
              width: '100%',
              maxWidth: '280px',
              flex: '0 1 280px',
              display: 'flex',
              gap: '16px',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: 'none',
            }}
          >
            <CardContent>
              <Typography variant="h3" sx={{ marginBottom: '12px' }}>
                {shareWithFriends.moreOffers}
              </Typography>
              <Typography variant="h3" color="primary">
                {shareWithFriends.comingSoon}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Paper>
  );
}
